import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { Bar, Range, Security, SecurityType } from '@/lib/types';
import {
  EARNINGS,
  NEWS,
  SECURITIES,
  generateBars,
  getFinancials,
  getNewsFor,
  getSecurity,
  movers,
  searchSecurities,
} from '@/mocks/market';
import {
  fetchBars,
  fetchEarnings,
  fetchFundamentals,
  fetchMovers,
  fetchNews,
  fetchSecurities,
  fetchSecuritiesBySymbols,
  fetchSecurity,
  searchLive,
  toEarnings,
  toFinancialYears,
  toNews,
  toSecurity,
} from '@/lib/api';

// VITE_DATA_MODE=live switches every hook to the BFF. The default stays on the
// fixture universe so local UI work never depends on Tasmania being reachable.
export const DATA_MODE = import.meta.env.VITE_DATA_MODE === 'live' ? 'live' : 'mock';
const LIVE = DATA_MODE === 'live';

export function useSecurities() {
  return useQuery<Security[]>({
    queryKey: ['securities', DATA_MODE],
    queryFn: async () => {
      if (!LIVE) return SECURITIES;
      const body = await fetchSecurities({ limit: 200, sort: 'symbol', dir: 'asc' });
      return body.items.map((item) => toSecurity(item));
    },
    ...(LIVE ? {} : { initialData: SECURITIES }),
    staleTime: 60_000,
  });
}

// useSecuritiesBySymbols hydrates a watchlist or portfolio in one request
// instead of one query per ticker.
export function useSecuritiesBySymbols(symbols: string[]) {
  const key = [...symbols].sort().join(',');
  const mock = symbols
    .map((symbol) => getSecurity(symbol))
    .filter((security): security is Security => Boolean(security));

  return useQuery<Security[]>({
    queryKey: ['securities-batch', key, DATA_MODE],
    queryFn: async () => {
      if (!LIVE) return mock;
      const items = await fetchSecuritiesBySymbols(symbols);
      return items.map((item) => toSecurity(item));
    },
    ...(LIVE ? {} : { initialData: mock }),
    enabled: symbols.length > 0,
    staleTime: 30_000,
  });
}

export function useSecurity(symbol: string | undefined) {
  const mock = symbol ? getSecurity(symbol) ?? null : null;
  return useQuery<Security | null>({
    queryKey: ['security', symbol, DATA_MODE],
    queryFn: async () => {
      if (!LIVE) return mock;
      if (!symbol) return null;
      const body = await fetchSecurity(symbol);
      return toSecurity(body.security, body.score, body.fundamentals);
    },
    ...(LIVE ? {} : { initialData: mock }),
    enabled: Boolean(symbol),
    staleTime: 30_000,
  });
}

export function useBars(symbol: string | undefined, range: Range) {
  const mock = symbol ? generateBars(symbol, range) : [];
  return useQuery<Bar[]>({
    queryKey: ['bars', symbol, range, DATA_MODE],
    queryFn: async () => {
      if (!LIVE) return mock;
      if (!symbol) return [];
      return fetchBars(symbol, range);
    },
    ...(LIVE ? {} : { initialData: mock }),
    enabled: Boolean(symbol),
    staleTime: 30_000,
  });
}

export function useMovers(kind: 'gainers' | 'losers' | 'active', limit = 8) {
  const mock = movers(kind, limit);
  return useQuery({
    queryKey: ['movers', kind, limit, DATA_MODE],
    queryFn: async () => {
      if (!LIVE) return mock;
      const items = await fetchMovers(kind, limit);
      return items.map((item) => toSecurity(item));
    },
    ...(LIVE ? {} : { initialData: mock }),
    staleTime: 30_000,
  });
}

export function useNews(symbol?: string, limit = 6) {
  const mock = symbol ? getNewsFor(symbol, limit) : NEWS.slice(0, limit);
  return useQuery({
    queryKey: ['news', symbol ?? 'all', limit, DATA_MODE],
    queryFn: async () => {
      if (!LIVE) return mock;
      const items = await fetchNews(symbol, limit);
      return items.map(toNews);
    },
    ...(LIVE ? {} : { initialData: mock }),
    staleTime: 120_000,
  });
}

export function useEarnings() {
  return useQuery({
    queryKey: ['earnings', DATA_MODE],
    queryFn: async () => {
      if (!LIVE) return EARNINGS;
      const items = await fetchEarnings();
      return items.map(toEarnings);
    },
    ...(LIVE ? {} : { initialData: EARNINGS }),
    staleTime: 300_000,
  });
}

export function useFinancials(security: Security | null | undefined) {
  const mock = security ? getFinancials(security) : [];
  return useQuery({
    queryKey: ['financials', security?.symbol, DATA_MODE],
    queryFn: async () => {
      if (!LIVE) return mock;
      if (!security) return [];
      const periods = await fetchFundamentals(security.symbol);
      return toFinancialYears(periods);
    },
    ...(LIVE ? {} : { initialData: mock }),
    enabled: Boolean(security),
    staleTime: 3_600_000,
  });
}

export function useSearch(query: string, limit = 8) {
  const mock = searchSecurities(query, limit);
  return useQuery<Security[]>({
    queryKey: ['search', query, limit, DATA_MODE],
    queryFn: async () => {
      if (!LIVE) return mock;
      const items = await searchLive(query, limit);
      return items.map((item) => toSecurity(item));
    },
    ...(LIVE ? {} : { initialData: mock }),
    enabled: query.trim().length > 0,
    staleTime: 30_000,
  });
}

export interface DirectoryParams {
  type: SecurityType;
  query: string;
  exchange: string;
  sector: string;
  pageSize?: number;
}

// useDirectory is the one paginated hook: the directory covers the whole
// listed universe, so filtering 11,578 rows in the browser is not an option.
export function useDirectory(params: DirectoryParams) {
  const pageSize = params.pageSize ?? 60;
  const mockFiltered = SECURITIES.filter((security) => {
    if (security.type !== params.type) return false;
    if (params.exchange !== 'all' && security.exchange !== params.exchange) return false;
    if (params.sector !== 'all' && security.sector !== params.sector) return false;
    if (params.query) {
      const q = params.query.toLowerCase();
      if (
        !security.symbol.toLowerCase().includes(q) &&
        !security.name.toLowerCase().includes(q)
      ) {
        return false;
      }
    }
    return true;
  });

  return useInfiniteQuery({
    queryKey: ['directory', params, DATA_MODE],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      if (!LIVE) {
        return {
          items: mockFiltered.slice(pageParam, pageParam + pageSize),
          total: mockFiltered.length,
          offset: pageParam,
        };
      }
      const body = await fetchSecurities({
        q: params.query,
        type: params.type,
        exchange: params.exchange === 'all' ? undefined : params.exchange,
        sector: params.sector === 'all' ? undefined : params.sector,
        limit: pageSize,
        offset: pageParam,
        sort: 'symbol',
        dir: 'asc',
      });
      return {
        items: body.items.map((item) => toSecurity(item)),
        total: body.total,
        offset: pageParam,
      };
    },
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    ...(LIVE
      ? { placeholderData: keepPreviousData }
      : {
          initialData: {
            pages: [{ items: mockFiltered.slice(0, pageSize), total: mockFiltered.length, offset: 0 }],
            pageParams: [0],
          },
        }),
    staleTime: 30_000,
  });
}
